import { cva } from 'class-variance-authority';

// =============================================================================
// Types
// =============================================================================

export type CardVariant = 'large' | 'default' | 'grid';

// =============================================================================
// CVA Variant Definitions
// =============================================================================

export const cardVariants = cva('overflow-hidden', {
  variants: {
    variant: {
      large: 'grid grid-cols-1 gap-0 lg:grid-cols-2',
      grid: 'grid grid-cols-1 gap-0 lg:grid-cols-2',
      default: 'flex h-full flex-col gap-0',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export const imageVariants = cva('relative block w-full overflow-hidden', {
  variants: {
    variant: {
      large: 'aspect-video lg:aspect-5/7',
      grid: 'aspect-video lg:aspect-5/7 lg:order-last',
      default: 'aspect-video',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export const containerVariants = cva('flex flex-col', {
  variants: {
    variant: {
      large: 'overflow-hidden py-6',
      grid: 'overflow-hidden py-6',
      default: 'flex-1 pb-6 pt-3',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export const contentVariants = cva('p-0', {
  variants: {
    variant: {
      large: 'mb-5 flex-1 lg:mb-0',
      grid: 'flex flex-1 flex-col justify-end',
      default: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// =============================================================================
// Variant Configuration
// =============================================================================

export const VARIANT_CONFIG = {
  large: {
    titleMaxLines: 2 as const,
    buttonSize: 'default' as const,
    showDescription: true,
    showDateOnTop: false,
  },
  grid: {
    titleMaxLines: 5 as const,
    buttonSize: 'default' as const,
    showDescription: true,
    showDateOnTop: true,
  },
  default: {
    titleMaxLines: 1 as const,
    buttonSize: 'default' as const,
    showDescription: false,
    showDateOnTop: false,
  },
} as const;

// =============================================================================
// Helpers
// =============================================================================

export function getTitleClampClass(
  maxLines: 1 | 2 | 5,
  variant: CardVariant
): string {
  if (maxLines === 1) {
    return variant === 'large' ? 'lg:line-clamp-1' : 'line-clamp-1';
  }
  if (maxLines === 2) {
    return variant === 'large' ? 'lg:line-clamp-2' : 'line-clamp-2';
  }
  return variant === 'large' ? 'lg:line-clamp-5' : 'line-clamp-5';
}

export function getDescriptionClampClass(variant: CardVariant): string {
  return variant === 'large'
    ? 'lg:line-clamp-2 xl:line-clamp-3'
    : 'line-clamp-3';
}
